type AvatarImage = {
  url: string;
  width: number;
};

type Avatar =
  | { __typename: "IconAvatar" }
  | { __typename: "PictureAvatar"; squareImages: ReadonlyArray<AvatarImage> };

type Principal = {
  __typename: string;
  displayName: string;
  avatar?: Avatar | null;
};

/** The width avatars are rendered at, at most, with room for high-density screens. */
const AVATAR_PICTURE_MIN_SIZE = 128;

/**
 * The URL of an avatar's square picture: the smallest one at least `minSize` pixels wide, or the
 * largest there is. Undefined for an icon avatar or none, which callers show as initials.
 */
export const avatarPictureUrl = (
  avatar: Avatar | null | undefined,
  minSize: number = AVATAR_PICTURE_MIN_SIZE,
): string | undefined => {
  if (avatar?.__typename !== "PictureAvatar") {
    return undefined;
  }
  // Square images come smallest first.
  const images = avatar.squareImages;
  const image =
    images.find(({ width }) => width >= minSize) ?? images[images.length - 1];
  return image?.url;
};

/** The URL of the picture a principal is shown as, if it has one. */
export const principalPictureUrl = (
  principal: Principal | null | undefined,
): string | undefined => avatarPictureUrl(principal?.avatar);

/** A person's email; undefined for other principals or when it was not selected. */
export const principalEmail = (
  principal: Principal | null | undefined,
): string | undefined => {
  if (!principal || !("email" in principal)) {
    return undefined;
  }
  return typeof principal.email === "string" ? principal.email : undefined;
};
